import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '@/components/ui/dialog';
import { 
  ArrowLeft, 
  Plus, 
  User, 
  Phone, 
  Calendar, 
  Trash2, 
  Loader2, 
  Edit, 
  X, 
  Save, 
  Search,
  ShoppingCart
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/use-auth';
import { useLocation } from '@/contexts/LocationContext';
import { productService } from '@/lib/packages';
import type { Database } from '@/integrations/supabase/types';
import { OnHoldItemWithDetails } from '@/hooks/use-onhold';

type ProductRow = Database['public']['Tables']['products']['Row'];
type CustomerRow = Database['public']['Tables']['customers']['Row'];

interface OnHoldLineItem {
  id: string;
  productId?: string;
  productName: string;
  productCode: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
}

interface OnHoldDetail {
  id: string; // Composite ID: customerId-date
  date: Date;
  customerId?: string;
  customerName: string;
  customerPhone: string;
  lineItems: OnHoldLineItem[];
  totalAmount: number;
}

export default function OnHoldDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { companyId, loading: authLoading, user } = useAuth();
  const { currentLocation } = useLocation();
  
  const [onHoldData, setOnHoldData] = useState<OnHoldDetail | null>(null);
  const [originalOnHoldData, setOriginalOnHoldData] = useState<OnHoldDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDeleteItemDialog, setShowDeleteItemDialog] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  
  // Edit mode states
  const [editingProductForItem, setEditingProductForItem] = useState<string | null>(null);
  const [editProductSearch, setEditProductSearch] = useState('');
  const [editProducts, setEditProducts] = useState<ProductRow[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Parse the ID to get customer ID and date
  const parseId = useCallback(() => {
    if (!id) return null;
    
    // ID format possibilities:
    // 1. Single onhold ID: "onhold_xxx"
    // 2. Composite ID: "customerId-date" (e.g., "uuid-2024-01-15")
    // 3. GroupKey format: "customer_uuid_2024-01-15_open" or "phone_123_2024-01-15_open"
    
    if (id.startsWith('onhold_')) {
      // Single onhold ID
      return { type: 'single', onHoldId: id.replace('onhold_', '') };
    }
    
    // Try to find date pattern (YYYY-MM-DD) anywhere in the ID
    const dateMatch = id.match(/(\d{4}-\d{2}-\d{2})/);
    if (dateMatch) {
      const datePart = dateMatch[1];
      
      // Check if it's a groupKey format like "customer_uuid_2024-01-15_open" or "phone_123_2024-01-15_open"
      if (id.startsWith('customer_')) {
        // Extract customerId from "customer_uuid" part
        // UUIDs might be split by underscores, so we need to reconstruct them
        const parts = id.split('_');
        if (parts.length >= 3) {
          // Find the date index
          const dateIndex = parts.findIndex(p => p.match(/^\d{4}-\d{2}-\d{2}$/));
          if (dateIndex > 1) {
            // Customer ID parts are between "customer" (index 0) and the date
            const customerIdParts = parts.slice(1, dateIndex);
            // UUID format is 8-4-4-4-12, so if we have 5 parts, join with dashes
            // Otherwise, it might be a single UUID string that wasn't split
            let customerId: string;
            if (customerIdParts.length === 1) {
              customerId = customerIdParts[0];
            } else {
              // Try to reconstruct UUID: typically 8-4-4-4-12 format
              // If we have 4 parts after "customer", it might be uuid split into 4 parts
              customerId = customerIdParts.join('-');
            }
            return { type: 'group', customerId, date: datePart };
          }
        }
      } else if (id.startsWith('phone_')) {
        // For phone-based grouping, we can't use it - need to fall back to originalIds
        // But we can try to extract the date
        return { type: 'date_only', date: datePart };
      }
      
      // Try to parse as customerId-date (direct UUID-date format)
      // UUID format: 8-4-4-4-12 chars (e.g., "550e8400-e29b-41d4-a716-446655440000")
      // Look for date at the end after a dash
      const endDateMatch = id.match(/(.+)-(\d{4}-\d{2}-\d{2})$/);
      if (endDateMatch) {
        const customerIdPart = endDateMatch[1];
        // Validate it looks like a UUID or customer ID (at least 8 chars)
        if (customerIdPart.length >= 8) {
          return { type: 'group', customerId: customerIdPart, date: endDateMatch[2] };
        }
      }
    }
    
    // If no date pattern found, treat as single onhold ID
    return { type: 'single', onHoldId: id };
  }, [id]);

  // Fetch onhold data
  const fetchOnHoldData = useCallback(async () => {
    if (!id || !companyId) return;
    
    const parsed = parseId();
    if (!parsed) {
      setError(`Geçersiz onhold ID: ${id}`);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      let query = supabase
        .from('on_holds')
        .select(`
          *,
          product:products(*),
          customer:customers(*)
        `)
        .eq('company_id', companyId)
        .eq('status', 'on_hold');
      
      if (parsed.type === 'single') {
        // Fetch single onhold item
        query = query.eq('id', parsed.onHoldId);
      } else if (parsed.type === 'group') {
        // Fetch all onhold items for customer and date
        query = query
          .eq('customer_id', parsed.customerId)
          .eq('date_on_hold', parsed.date);
      } else if (parsed.type === 'date_only') {
        // Can't fetch by customer, so return error
        setError(`Müşteri bilgisi bulunamadı (ID: ${id})`);
        setLoading(false);
        return;
      }
      
      const { data: onHoldItems, error: onHoldError } = await query.order('date_on_hold', { ascending: false });
      
      if (onHoldError) {
        console.error('Error fetching onhold items:', onHoldError);
        throw onHoldError;
      }
      
      if (!onHoldItems || onHoldItems.length === 0) {
        console.log('No onhold items found for:', { id, parsed, companyId });
        setError(`Beklemedeki satış bulunamadı (ID: ${id})`);
        return;
      }
      
      // Get customer info from first item
      const firstItem = onHoldItems[0] as any;
      const customer = firstItem.customer;
      
      // Transform to OnHoldDetail format
      const lineItems: OnHoldLineItem[] = onHoldItems.map((item: any) => ({
        id: item.id,
        productId: item.product_id,
        productName: item.product?.name || 'Bilinmeyen Ürün',
        productCode: item.product?.item_code || '',
        quantity: item.hold_quantity || 1,
        unitPrice: item.hold_price || 0,
        subtotal: (item.hold_quantity || 1) * (item.hold_price || 0)
      }));
      
      const totalAmount = lineItems.reduce((sum, item) => sum + item.subtotal, 0);
      
      const onHoldDetail: OnHoldDetail = {
        id: id,
        date: new Date(firstItem.date_on_hold),
        customerId: firstItem.customer_id,
        customerName: customer 
          ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Bilinmeyen Müşteri'
          : 'Bilinmeyen Müşteri',
        customerPhone: customer?.phone || '',
        lineItems,
        totalAmount
      };
      
      setOnHoldData(onHoldDetail);
      setOriginalOnHoldData(JSON.parse(JSON.stringify(onHoldDetail)));
    } catch (err: any) {
      console.error('Error fetching onhold data:', err);
      setError(err.message || 'Beklemedeki satış yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [id, companyId, parseId]);

  useEffect(() => {
    if (companyId && !authLoading) {
      fetchOnHoldData();
    }
  }, [companyId, authLoading, fetchOnHoldData]);

  // Search products
  useEffect(() => {
    if (editProductSearch.length >= 2 && companyId) {
      const timeoutId = setTimeout(async () => {
        setLoadingProducts(true);
        try {
          const products = await productService.searchProducts(companyId, editProductSearch, 20);
          setEditProducts(products || []);
        } catch (err) {
          console.error('Error searching products:', err);
          setEditProducts([]);
        } finally {
          setLoadingProducts(false);
        }
      }, 300);
      
      return () => clearTimeout(timeoutId);
    } else {
      setEditProducts([]);
    }
  }, [editProductSearch, companyId]);

  const handleUpdateLineItem = (itemId: string, updates: Partial<OnHoldLineItem>) => {
    if (!onHoldData) return;
    setOnHoldData({
      ...onHoldData,
      lineItems: onHoldData.lineItems.map(item => {
        if (item.id === itemId) {
          const updated = { ...item, ...updates };
          // Recalculate subtotal
          updated.subtotal = updated.quantity * updated.unitPrice;
          return updated;
        }
        return item;
      })
    });
  };

  const handleSelectProduct = (itemId: string, product: ProductRow) => {
    handleUpdateLineItem(itemId, {
      productId: product.id,
      productName: product.name,
      productCode: product.item_code || '',
      unitPrice: product.price || 0
    });
    setEditProductSearch('');
    setEditProducts([]);
    setEditingProductForItem(null);
  };

  const handleAddLineItem = () => {
    if (!onHoldData) return;
    const newItem: OnHoldLineItem = {
      id: `temp-${Date.now()}`,
      productId: '',
      productName: '',
      productCode: '',
      quantity: 1,
      unitPrice: 0,
      subtotal: 0
    };
    setOnHoldData({
      ...onHoldData,
      lineItems: [...onHoldData.lineItems, newItem]
    });
  };

  const handleRemoveLineItem = (itemId: string) => {
    if (!onHoldData) return;
    setOnHoldData({
      ...onHoldData,
      lineItems: onHoldData.lineItems.filter(item => item.id !== itemId)
    });
  };

  const handleCancelEdit = () => {
    if (originalOnHoldData) {
      setOnHoldData(JSON.parse(JSON.stringify(originalOnHoldData)));
    }
    setIsEditMode(false);
    setEditProductSearch('');
    setEditingProductForItem(null);
    setEditProducts([]);
  };

  const handleUpdateOnHold = async () => {
    if (!onHoldData || !companyId) return;
    
    // Validate all items have product selected
    const itemsWithoutProduct = onHoldData.lineItems.filter(item => !item.productId);
    if (itemsWithoutProduct.length > 0) {
      toast({
        title: "Hata",
        description: "Tüm ürünler seçilmelidir",
        variant: "destructive",
      });
      return;
    }
    
    if (!onHoldData.customerId) {
      toast({
        title: "Hata",
        description: "Müşteri bilgisi bulunamadı",
        variant: "destructive",
      });
      return;
    }
    
    setSaving(true);
    try {
      // Get product costs for new items
      const productIds = onHoldData.lineItems
        .filter(item => item.id.startsWith('temp-') && item.productId)
        .map(item => item.productId!);
      
      const productCostsMap = new Map<string, number>();
      if (productIds.length > 0) {
        const { data: products } = await supabase
          .from('products')
          .select('id, current_cost, initial_cost')
          .in('id', productIds)
          .eq('company_id', companyId);
        
        if (products) {
          products.forEach(product => {
            productCostsMap.set(product.id, product.current_cost || product.initial_cost || 0);
          });
        }
      }
      
      // Update each onhold item
      for (const item of onHoldData.lineItems) {
        if (item.id.startsWith('temp-')) {
          // New item - create it
          if (!item.productId) continue; // Skip if no product (shouldn't happen due to validation)
          
          const productCost = productCostsMap.get(item.productId) || 0;
          
          const { error: createError } = await supabase
            .from('on_holds')
            .insert({
              company_id: companyId,
              location_id: currentLocation?.id || null,
              customer_id: onHoldData.customerId,
              product_id: item.productId,
              hold_quantity: item.quantity,
              hold_price: item.unitPrice,
              hold_cost: productCost,
              status: 'on_hold',
              date_on_hold: format(onHoldData.date, 'yyyy-MM-dd')
            });
          
          if (createError) throw createError;
        } else {
          // Existing item - update it
          if (!item.productId) {
            // If product was removed, delete the item
            const { error: deleteError } = await supabase
              .from('on_holds')
              .update({ status: 'returned' })
              .eq('id', item.id)
              .eq('company_id', companyId);
            
            if (deleteError) throw deleteError;
            continue;
          }
          
          const { error: updateError } = await supabase
            .from('on_holds')
            .update({
              product_id: item.productId,
              hold_quantity: item.quantity,
              hold_price: item.unitPrice
            })
            .eq('id', item.id)
            .eq('company_id', companyId);
          
          if (updateError) throw updateError;
        }
      }
      
      // Delete items that were removed
      const originalItemIds = new Set(originalOnHoldData?.lineItems.map(i => i.id) || []);
      const currentItemIds = new Set(onHoldData.lineItems.map(i => i.id));
      const removedItemIds = Array.from(originalItemIds).filter(id => !currentItemIds.has(id) && !id.startsWith('temp-'));
      
      for (const removedId of removedItemIds) {
        const { error: deleteError } = await supabase
          .from('on_holds')
          .update({ status: 'returned' })
          .eq('id', removedId)
          .eq('company_id', companyId);
        
        if (deleteError) throw deleteError;
      }
      
      toast({
        title: "Güncellendi",
        description: "Beklemedeki satış başarıyla güncellendi",
      });
      
      setIsEditMode(false);
      await fetchOnHoldData();
    } catch (err: any) {
      console.error('Error updating onhold:', err);
      toast({
        title: "Hata",
        description: err.message || "Güncelleme sırasında bir hata oluştu",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!companyId) return;
    
    setSaving(true);
    try {
      const { error } = await supabase
        .from('on_holds')
        .update({ status: 'returned' })
        .eq('id', itemId)
        .eq('company_id', companyId);
      
      if (error) throw error;
      
      toast({
        title: "Silindi",
        description: "Ürün başarıyla silindi",
      });
      
      setShowDeleteItemDialog(null);
      await fetchOnHoldData();
      
      // If no items left, navigate back
      if (onHoldData && onHoldData.lineItems.length === 1) {
        navigate('/sales');
      }
    } catch (err: any) {
      console.error('Error deleting item:', err);
      toast({
        title: "Hata",
        description: err.message || "Silme işlemi başarısız",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAll = async () => {
    if (!onHoldData || !companyId) return;
    
    setSaving(true);
    try {
      // Delete all items
      const itemIds = onHoldData.lineItems.map(item => item.id).filter(id => !id.startsWith('temp-'));
      
      for (const itemId of itemIds) {
        const { error } = await supabase
          .from('on_holds')
          .update({ status: 'returned' })
          .eq('id', itemId)
          .eq('company_id', companyId);
        
        if (error) throw error;
      }
      
      toast({
        title: "Silindi",
        description: "Beklemedeki satış başarıyla silindi",
      });
      
      // Navigate back to sales list
      navigate('/sales');
    } catch (err: any) {
      console.error('Error deleting onhold:', err);
      toast({
        title: "Hata",
        description: err.message || "Silme işlemi başarısız",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
      setShowDeleteDialog(false);
    }
  };

  // Loading state
  if (loading || authLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex items-center gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            <span className="text-muted-foreground">Beklemedeki satış detayları yükleniyor...</span>
          </div>
        </div>
      </Layout>
    );
  }

  // Error state
  if (error || !onHoldData) {
    return (
      <Layout>
        <div className="space-y-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/sales')}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Satışlara Dön
          </Button>
          
          <div className="text-center py-12">
            <h3 className="text-lg font-medium mb-2">Beklemedeki satış bulunamadı</h3>
            <p className="text-muted-foreground mb-4">{error || 'Bu beklemedeki satış mevcut değil'}</p>
            <div className="flex gap-2 justify-center">
              <Button onClick={fetchOnHoldData} variant="outline">
                Tekrar Dene
              </Button>
              <Button onClick={() => navigate('/sales')}>
                Satışlara Dön
              </Button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const totalAmount = onHoldData.lineItems.reduce((sum, item) => sum + item.subtotal, 0);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/sales')}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Satışlara Dön
          </Button>
          
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold">Beklemedeki Satış - {onHoldData.customerName}</h1>
              <Badge variant="outline" className="bg-orange-50 text-orange-600 border-orange-200 dark:bg-orange-950/30 dark:text-orange-500 dark:border-orange-800">
                Beklemede
              </Badge>
            </div>
            <p className="text-muted-foreground">
              {format(onHoldData.date, 'd MMMM yyyy', { locale: tr })}
            </p>
          </div>

          <div className="flex gap-2">
            {!isEditMode ? (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => setIsEditMode(true)}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Düzenle
                </Button>
                <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                  <DialogTrigger asChild>
                    <Button variant="destructive">
                      <Trash2 className="w-4 h-4 mr-2" />
                      Tümünü Sil
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Tümünü Sil</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                        <p className="text-sm text-destructive-foreground">
                          <strong>Uyarı:</strong> Bu işlem geri alınamaz. Tüm beklemedeki ürünler silinecektir.
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" className="flex-1" onClick={() => setShowDeleteDialog(false)}>
                          Vazgeç
                        </Button>
                        <Button 
                          variant="destructive" 
                          className="flex-1" 
                          onClick={handleDeleteAll}
                          disabled={saving}
                        >
                          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                          Tümünü Sil
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </>
            ) : (
              <>
                <Button 
                  variant="outline" 
                  onClick={handleCancelEdit}
                  disabled={saving}
                >
                  <X className="w-4 h-4 mr-2" />
                  İptal
                </Button>
                <Button 
                  onClick={handleUpdateOnHold}
                  disabled={saving}
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Kaydet
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Customer Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Müşteri Bilgileri
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium">{onHoldData.customerName}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <span>{onHoldData.customerPhone}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Line Items */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Ürünler ({onHoldData.lineItems.length})</CardTitle>
                  {isEditMode && (
                    <Button variant="outline" size="sm" onClick={handleAddLineItem}>
                      <Plus className="w-4 h-4 mr-2" />
                      Ürün Ekle
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {onHoldData.lineItems.map((item) => (
                    <div key={item.id} className="flex items-start justify-between p-3 border rounded-lg">
                      {isEditMode ? (
                        <div className="flex-1 space-y-3">
                          <div>
                            <Label>Ürün</Label>
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                              <Input
                                value={item.productName || ''}
                                onChange={(e) => {
                                  if (!item.productId) {
                                    setEditingProductForItem(item.id);
                                    setEditProductSearch(e.target.value);
                                    handleUpdateLineItem(item.id, { productName: e.target.value });
                                  }
                                }}
                                onFocus={() => {
                                  if (!item.productId) {
                                    setEditingProductForItem(item.id);
                                  }
                                }}
                                placeholder="Ürün adı veya kod ile ara..."
                                className="pl-9"
                              />
                            </div>
                            {editProductSearch.length >= 2 && editingProductForItem === item.id && !item.productId && (
                              <div className="mt-2 border rounded-lg max-h-48 overflow-y-auto">
                                {loadingProducts ? (
                                  <div className="p-4 text-center text-sm text-muted-foreground">
                                    <Loader2 className="w-4 h-4 animate-spin mx-auto mb-2" />
                                    Aranıyor...
                                  </div>
                                ) : editProducts.length > 0 ? (
                                  editProducts.map(product => (
                                    <div
                                      key={product.id}
                                      className="p-3 hover:bg-muted cursor-pointer border-b last:border-b-0"
                                      onClick={() => handleSelectProduct(item.id, product)}
                                    >
                                      <div className="font-medium">{product.name}</div>
                                      {product.item_code && (
                                        <div className="text-sm text-muted-foreground">{product.item_code}</div>
                                      )}
                                      <div className="text-sm font-medium">₺{product.price?.toLocaleString('tr-TR') || '0'}</div>
                                    </div>
                                  ))
                                ) : (
                                  <div className="p-4 text-center text-sm text-muted-foreground">
                                    Ürün bulunamadı
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label>Miktar</Label>
                              <Input
                                type="number"
                                value={item.quantity}
                                onChange={(e) => handleUpdateLineItem(item.id, { quantity: parseFloat(e.target.value) || 0 })}
                                min="0"
                                step="1"
                              />
                            </div>
                            <div>
                              <Label>Birim Fiyat</Label>
                              <Input
                                type="number"
                                value={item.unitPrice}
                                onChange={(e) => handleUpdateLineItem(item.id, { unitPrice: parseFloat(e.target.value) || 0 })}
                                min="0"
                                step="0.01"
                              />
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">
                              Toplam: ₺{item.subtotal.toLocaleString('tr-TR')}
                            </span>
                            <div className="flex gap-2">
                              {!item.id.startsWith('temp-') && (
                                <Dialog open={showDeleteItemDialog === item.id} onOpenChange={(open) => setShowDeleteItemDialog(open ? item.id : null)}>
                                  <DialogTrigger asChild>
                                    <Button variant="ghost" size="sm">
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </DialogTrigger>
                                  <DialogContent>
                                    <DialogHeader>
                                      <DialogTitle>Ürünü Sil</DialogTitle>
                                    </DialogHeader>
                                    <div className="space-y-4">
                                      <p>Bu ürünü silmek istediğinizden emin misiniz?</p>
                                      <div className="flex gap-2">
                                        <Button variant="outline" className="flex-1" onClick={() => setShowDeleteItemDialog(null)}>
                                          Vazgeç
                                        </Button>
                                        <Button 
                                          variant="destructive" 
                                          className="flex-1" 
                                          onClick={() => handleDeleteItem(item.id)}
                                          disabled={saving}
                                        >
                                          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                                          Sil
                                        </Button>
                                      </div>
                                    </div>
                                  </DialogContent>
                                </Dialog>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveLineItem(item.id)}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex-1">
                            <h4 className="font-medium">{item.productName}</h4>
                            <p className="text-sm text-muted-foreground">{item.productCode}</p>
                          </div>
                          
                          <div className="text-right">
                            <div className="flex items-center gap-4 text-sm">
                              <span>{item.quantity} × ₺{item.unitPrice.toLocaleString('tr-TR')}</span>
                              <span className="font-medium">₺{item.subtotal.toLocaleString('tr-TR')}</span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Summary Sidebar */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Özet</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span>Toplam:</span>
                  <span className="text-lg font-semibold">₺{totalAmount.toLocaleString('tr-TR')}</span>
                </div>
                <Separator />
                <div className="text-sm text-muted-foreground">
                  {onHoldData.lineItems.length} ürün beklemede
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}

